import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { toast } from 'sonner';
import { Key, Plus, Trash2, Copy, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '@/components/ui/dialog';

const SCOPE_OPTIONS = [
  { value: 'read', label: 'Read' },
  { value: 'write', label: 'Write' },
];

const APIKeys = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState(['read']);
  const [createdKey, setCreatedKey] = useState(null);

  useEffect(() => {
    if (isAdmin) fetchKeys();
    else setLoading(false);
  }, [isAdmin]);

  const fetchKeys = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api-keys`);
      setKeys(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const toggleScope = (s) => {
    setScopes((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (scopes.length === 0) {
      toast.error('Pick at least one scope');
      return;
    }
    try {
      const r = await axios.post(`${API}/api-keys`, { name, scopes });
      setCreatedKey(r.data);
      setName('');
      setScopes(['read']);
      fetchKeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create key');
    }
  };

  const handleRevoke = async (id, label) => {
    if (!window.confirm(`Revoke API key "${label}"? Any apps using it will stop working.`)) return;
    try {
      await axios.delete(`${API}/api-keys/${id}`);
      toast.success('Key revoked');
      fetchKeys();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revoke');
    }
  };

  const copyKey = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Copied to clipboard'));
  };

  if (!isAdmin) {
    return (
      <div className="p-8 md:p-12" data-testid="apikeys-page">
        <h1 className="text-4xl font-chivo font-bold text-slate-900 tracking-tight">API Keys</h1>
        <p className="mt-3 text-slate-600 font-ibm">Admin access is required to manage API keys.</p>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-12" data-testid="apikeys-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Key size={32} className="text-slate-900" />
            <div>
              <h1 className="text-4xl font-chivo font-bold text-slate-900 tracking-tight" data-testid="apikeys-title">
                API Keys
              </h1>
              <p className="text-slate-600 mt-1 font-ibm">
                Programmatic access to your organization data. Pass as <code className="text-xs">X-API-Key</code> header.
              </p>
            </div>
          </div>
          <Dialog
            open={dialogOpen}
            onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) setCreatedKey(null);
            }}
          >
            <DialogTrigger asChild>
              <Button
                data-testid="create-apikey-button"
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm hover:scale-[0.98] transition-transform"
              >
                <Plus size={16} className="mr-2" /> New Key
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white rounded-sm max-w-md">
              <DialogHeader>
                <DialogTitle className="font-chivo text-2xl">Create API Key</DialogTitle>
                <DialogDescription className="text-slate-600 font-ibm text-sm">
                  Choose a name and the scopes this key may use. You will only see the secret once.
                </DialogDescription>
              </DialogHeader>

              {createdKey ? (
                <div className="space-y-3" data-testid="apikey-created-section">
                  <div className="flex items-center gap-2 text-emerald-700">
                    <ShieldCheck size={18} />
                    <span className="font-ibm font-semibold text-sm">Save this key now — it cannot be retrieved later.</span>
                  </div>
                  <div className="bg-slate-900 text-emerald-300 font-mono text-xs p-3 rounded-sm break-all flex items-center justify-between gap-2">
                    <span data-testid="apikey-secret">{createdKey.key}</span>
                    <button
                      onClick={() => copyKey(createdKey.key)}
                      data-testid="apikey-copy"
                      className="p-1 hover:bg-slate-800 rounded-sm"
                    >
                      <Copy size={14} />
                    </button>
                  </div>
                  <Button
                    onClick={() => setDialogOpen(false)}
                    data-testid="apikey-done"
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
                  >
                    Done
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleCreate} className="space-y-4" data-testid="apikey-form">
                  <div>
                    <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">Name</Label>
                    <Input
                      data-testid="apikey-name-input"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. Zapier import"
                      className="mt-2 bg-slate-50 border-slate-200 rounded-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-900 font-ibm font-semibold uppercase text-xs tracking-widest">Scopes</Label>
                    <div className="mt-2 flex gap-3">
                      {SCOPE_OPTIONS.map((s) => (
                        <label
                          key={s.value}
                          className={`px-3 py-2 border rounded-sm cursor-pointer font-ibm text-sm flex items-center gap-2 ${
                            scopes.includes(s.value)
                              ? 'border-slate-900 bg-slate-900 text-white'
                              : 'border-slate-300 text-slate-700 hover:border-slate-500'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={scopes.includes(s.value)}
                            onChange={() => toggleScope(s.value)}
                            data-testid={`apikey-scope-${s.value}`}
                            className="hidden"
                          />
                          {s.label}
                        </label>
                      ))}
                    </div>
                  </div>
                  <Button
                    type="submit"
                    data-testid="apikey-submit-button"
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
                  >
                    Create Key
                  </Button>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-slate-600 font-ibm" data-testid="apikeys-loading">Loading…</div>
        ) : keys.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-sm p-12 text-center" data-testid="apikeys-empty">
            <Key className="mx-auto text-slate-400 mb-4" size={48} />
            <h3 className="text-xl font-chivo font-bold text-slate-900 mb-2">No API keys yet</h3>
            <p className="text-slate-600 font-ibm text-sm">
              Generate a key to authenticate external integrations against your organization data.
            </p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="apikeys-table">
            <table className="w-full">
              <thead className="bg-slate-900 text-white">
                <tr>
                  <th className="px-4 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">Name</th>
                  <th className="px-4 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">Prefix</th>
                  <th className="px-4 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">Scopes</th>
                  <th className="px-4 py-4 text-left text-xs font-ibm font-semibold uppercase tracking-widest">Created</th>
                  <th className="px-4 py-4 text-right text-xs font-ibm font-semibold uppercase tracking-widest">Actions</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k, idx) => (
                  <tr key={k.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`apikey-row-${idx}`}>
                    <td className="px-4 py-3 font-ibm text-slate-900 text-sm">{k.name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{k.key_prefix}…</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(k.scopes || []).map((s) => (
                          <span
                            key={s}
                            className="text-[10px] font-ibm font-semibold uppercase tracking-widest px-2 py-0.5 rounded-sm bg-slate-100 text-slate-700"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-ibm text-xs text-slate-600">
                      {new Date(k.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleRevoke(k.id, k.name)}
                        data-testid={`apikey-revoke-${idx}`}
                        className="p-2 hover:bg-red-50 rounded-sm transition-colors"
                        title="Revoke"
                      >
                        <Trash2 size={14} className="text-red-600" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default APIKeys;
